import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <nav className="fixed top-0 w-full z-40 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/welcome')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-8 h-8 text-teal-500" />
          <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
        </div>
        
        <p className="text-slate-400 mb-8">Last updated: February 2026</p>

        <div className="space-y-8 text-slate-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
            <p>By accessing or using the WellNest Technologies platform ("Service"), you agree to be bound by these Terms of Service. If you disagree with any part, you may not access the Service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Description of Service</h2>
            <p>WellNest Technologies provides a cloud-based spa management platform that includes:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Booking and scheduling management</li>
              <li>Therapist and staff management</li>
              <li>Customer relationship management</li>
              <li>Revenue and analytics reporting</li>
              <li>WhatsApp and email communication tools</li>
              <li>Multi-location management</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Account Registration</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You must provide accurate and complete registration information</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must be at least 18 years old to use this Service</li>
              <li>One person or legal entity may maintain only one account</li>
              <li>You are responsible for all activities under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Subscription & Billing</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Subscriptions are billed in advance on a monthly or annual basis</li>
              <li>All fees are exclusive of applicable taxes</li>
              <li>Prices may change with 30 days notice</li>
              <li>No refunds for partial months of service</li>
              <li>Free trial period is 14 days with full feature access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Acceptable Use</h2>
            <p>You agree NOT to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Use the Service for any illegal purposes</li>
              <li>Attempt to gain unauthorized access to other accounts</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Upload malicious code or content</li>
              <li>Resell or redistribute the Service without authorization</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Data Ownership</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You retain ownership of all data you upload to the Service</li>
              <li>We do not claim ownership of your business data</li>
              <li>You grant us license to process your data to provide the Service</li>
              <li>You can export your data at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Service Availability</h2>
            <p>We strive for 99.9% uptime but do not guarantee uninterrupted service. We are not liable for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Scheduled maintenance (announced in advance)</li>
              <li>Circumstances beyond our control</li>
              <li>Third-party service outages (payment processors, hosting)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, WellNest Technologies shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Termination</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You may cancel your subscription at any time</li>
              <li>We may suspend accounts for Terms violations</li>
              <li>Upon termination, your data will be available for export for 30 days</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Governing Law</h2>
            <p>These Terms shall be governed by the laws of India. Any disputes shall be resolved in the courts of Agra, Uttar Pradesh.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Contact</h2>
            <div className="mt-2 p-4 bg-slate-900 rounded-lg">
              <p><strong>Email:</strong> legal@wellnesttechnologies.com</p>
              <p><strong>Address:</strong> Agra, Uttar Pradesh, India</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
